import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CutStepVideoAccepted, ManualBody, ManualState, ManualStepType, SetManualSettingsBody, StepPayload } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { manualApi, saveEditedStepImage as saveEditedStepImageApi } from "../../api/client.js";
import { invalidateDashboard } from "../dashboard/dashboard-cache.js";

/** A failed manual mutation — carries the HTTP status + the contract's machine `code` for friendly copy. */
export interface ManualMutationError extends Error {
  status: number;
  code?: string;
  body?: unknown;
}

function fail(status: number, body: unknown): ManualMutationError {
  const err = new Error(`manual mutation failed (${status})`) as ManualMutationError;
  err.status = status;
  err.code = (body as { code?: string } | undefined)?.code;
  err.body = body;
  return err;
}

/** Every manual lifecycle change affects the manuals lists (active + archived, any facility), the tree's manual counts,
 *  the global manual SEARCH results (FN4b-2), AND the global "Manual favourites" rail (MFG) — invalidate ALL those query
 *  prefixes so the grid, the facility cards, any open search view, and the rail refetch. Without the `manual-search` /
 *  `manual-favourites-list` invalidations, archiving/deleting/moving a manual then reopening the same search or the
 *  Manual-favourites chip within the staleTime would render the now-inactive/moved manual as a live card (panel).
 *
 *  EXPORTED because a manual's card image is its CATEGORY's icon (`COALESCE(category.image_id,
 *  realm.standard_category_icon_id)`), so changing an icon changes every card in every facility that category is
 *  attached to — `useCategoryMutations` calls this rather than hand-listing the same keys and drifting from it
 *  (ADR-043 Phase 2). */
export function invalidateManuals(qc: ReturnType<typeof useQueryClient>, session: AuthSession | null): void {
  void qc.invalidateQueries({ queryKey: ["facility-manuals", session?.realm, session?.sub] });
  void qc.invalidateQueries({ queryKey: ["facility-manual-trash", session?.realm, session?.sub] });
  void qc.invalidateQueries({ queryKey: ["facilities", session?.realm, session?.sub] });
  void qc.invalidateQueries({ queryKey: ["manual-search", session?.realm, session?.sub] });
  void qc.invalidateQueries({ queryKey: ["manual-favourites-list", session?.realm, session?.sub] });
  invalidateDashboard(qc, session); // the Dashboard's list pages + the Übersicht counts (ADR-034)
}

export function useArchiveManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom }: { id: number; expectedFrom: ManualState }) => {
      // The state the card/drawer SHOWED — a stale belief is a 409 the stale-handling turns into a refresh.
      const res = await manualApi.archiveManual({ params: { id }, body: { expectedFrom } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => invalidateManualsAndLinks(qc, session), // ADR-020 §6: a parent's archive cascades to its linked children's cards
  });
}

export function useUnarchiveManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom }: { id: number; expectedFrom: ManualState }) => {
      const res = await manualApi.unarchiveManual({ params: { id }, body: { expectedFrom } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    // ADR-020 §6: a parent's archive cascades to its linked children's cards. Also after a failure: a 409 means the list was out of date.
    onSettled: () => invalidateManualsAndLinks(qc, session),
  });
}

/** Move a manual to the trash (ADR-056). Linked copies follow, so their cards and link lists refresh too. */
export function useTrashManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom }: { id: number; expectedFrom: ManualState }) => {
      const res = await manualApi.trashManual({ params: { id }, body: { expectedFrom } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    // Also after a failure: a 409 means the list was out of date.
    onSettled: () => invalidateManualsAndLinks(qc, session),
  });
}

/** Restore a manual from the trash (ADR-056). */
export function useRestoreManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom }: { id: number; expectedFrom: ManualState }) => {
      const res = await manualApi.restoreManual({ params: { id }, body: { expectedFrom } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    // Also after a failure: a 409 means the list was out of date.
    onSettled: () => invalidateManualsAndLinks(qc, session),
  });
}

export function useDuplicateManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await manualApi.duplicateManual({ params: { id }, body: {} });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => invalidateManuals(qc, session),
  });
}

export interface CreateManualVars {
  facilityId: number;
  categoryId: number;
  name: string;
  /** Rich-text HTML (sanitized server-side). Omitted when the editor is empty. */
  description?: string;
  is2dWebManual: boolean;
  is3dWebManual: boolean;
  isVrManual: boolean;
  isArManual: boolean;
}

/** Create a draft manual in a facility's category (ADR-013). Invalidates the manuals lists + the tree counts. */
export function useCreateManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ facilityId, ...body }: CreateManualVars) => {
      const res = await manualApi.createManual({ params: { id: facilityId }, body });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => invalidateManuals(qc, session),
  });
}

export interface MoveManualVars {
  id: number;
  facilityId: number;
  categoryId: number;
}

export function useMoveManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, facilityId, categoryId }: MoveManualVars) => {
      const res = await manualApi.moveManual({ params: { id }, body: { facilityId, categoryId } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => invalidateManuals(qc, session),
  });
}

/**
 * The pre-delete snapshot (`GET /manuals/:id/stats`). `staleTime: 0` + `gcTime: 0` so a reopen never serves a cached,
 * stale `expected` token (re-checked server-side under the topology lock). `retry: false` so a 403/404 surfaces at once.
 */
export function useManualStats(session: AuthSession | null, id: number | null) {
  return useQuery({
    queryKey: ["manual-stats", session?.realm, session?.sub, id],
    enabled: session !== null && id !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    queryFn: async () => {
      const res = await manualApi.manualStats({ params: { id: id! } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
  });
}

/** The reviewers assigned to a manual (ADR-019, read-only) — lazily fetched when the details drawer opens (`id` set).
 *  Keyed per (realm, user, manual) so it refetches across manuals; a modest default staleTime is fine (read-only). */
export function useManualAssignedUsers(session: AuthSession | null, id: number | null) {
  return useQuery({
    queryKey: ["manual-assigned-users", session?.realm, session?.sub, id],
    enabled: session !== null && id !== null,
    queryFn: async () => {
      const res = await manualApi.getManualAssignedUsers({ params: { id: id! } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body.users;
    },
  });
}

/**
 * The manual's HAND-SPEED rig (ADR-039): the flag, the devices bound to it, and every `HandSpeedSensor` the realm
 * could bind. Gated server-side on the manual's own EDIT authz (D2), so it is fetched only where the caller may edit
 * — never speculatively from a view-only drawer, where it would 403 on every open.
 */
export function useManualHandspeed(session: AuthSession | null, id: number | null, enabled: boolean) {
  return useQuery({
    queryKey: handspeedKey(session, id),
    enabled: session !== null && id !== null && enabled,
    queryFn: async () => {
      const res = await manualApi.getManualHandspeed({ params: { id: id! } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
  });
}

/** The manual's own settings (ADR-040) — name, description, isArManual, isSensorManual, in one partial PATCH. */
export function useSetManualSettings(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & SetManualSettingsBody) => {
      const res = await manualApi.setManualSettings({ params: { id }, body });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: (_data, vars) => {
      invalidateManuals(qc, session); // name, description and the type badges ride on every card
      // The platforms decide whether the open body has elements (ADR-057), so the editor must refetch it.
      void qc.invalidateQueries({ queryKey: ["manual-body", session?.realm, session?.sub, vars.id] });
      // Turning sensor usage off clears `handspeed_check` server-side (ADR-040 D3), so a cached rig read is stale.
      void qc.invalidateQueries({ queryKey: ["manual-handspeed", session?.realm, session?.sub, vars.id] });
    },
  });
}

/** Every hand-speed write shares this key, so any control can see that another one is in flight (ADR-039). */
export const HANDSPEED_MUTATION_KEY = ["manual-handspeed-write"] as const;

/** The flag and the device list travel together in ONE request, as the legacy's single PATCH does (ADR-039 D5). */
export function useSetManualHandspeed(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: HANDSPEED_MUTATION_KEY,
    mutationFn: async ({ id, enabled, sensorIds }: { id: number; enabled: boolean; sensorIds: number[] }) => {
      const res = await manualApi.setManualHandspeed({ params: { id }, body: { enabled, sensorIds } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: async (data, vars) => {
      // CANCEL FIRST. `staleTime` is 30s, and opening the picker mounts a second observer on this key — which starts
      // a background GET once the data is stale. A GET that resolves AFTER the seed would overwrite the authoritative
      // PATCH response with pre-save data, and the next switch toggle would then PATCH those stale ids and drop the
      // devices just saved (diff panel).
      await qc.cancelQueries({ queryKey: handspeedKey(session, vars.id) });
      // The response IS the new rig — seed it rather than refetching, so the section and the open picker agree
      // immediately (they share this key) instead of flickering through a stale list.
      qc.setQueryData(handspeedKey(session, vars.id), data);
      invalidateManuals(qc, session); // `handspeedCheck` rides on every manual card + the drawer's type badges
    },
  });
}

function handspeedKey(session: AuthSession | null, id: number | null) {
  return ["manual-handspeed", session?.realm, session?.sub, id] as const;
}

/** Candidate reviewers for a manual (ADR-022) — lazily fetched, only while the Manage-reviewers dialog is open (`enabled`).
 *  `confirm`-gated server-side; the caller passes `enabled` so we don't fire a 403-prone request from a non-confirm view. */
export function useAssignableUsers(session: AuthSession | null, id: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ["manual-assignable-users", session?.realm, session?.sub, id],
    enabled: session !== null && id !== null && enabled,
    queryFn: async () => {
      const res = await manualApi.getAssignableUsers({ params: { id: id! } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body.users;
    },
  });
}

/** Invalidate BOTH reviewer queries for a manual after an assign/unassign — the drawer's read-only "Assigned users" list
 *  AND the dialog's candidate list (a newly-assigned user must drop out of the candidates, and vice-versa). */
function invalidateReviewers(qc: ReturnType<typeof useQueryClient>, session: AuthSession | null): void {
  void qc.invalidateQueries({ queryKey: ["manual-assigned-users", session?.realm, session?.sub] });
  // …and the caller's own "Assigned to me" set, which the same writes change.
  void qc.invalidateQueries({ queryKey: ["manual-assignments", session?.realm, session?.sub] });
  void qc.invalidateQueries({ queryKey: ["manual-assignable-users", session?.realm, session?.sub] });
}

export function useAssignManualUser(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: number; userId: number }) => {
      const res = await manualApi.assignManualUser({ params: { id }, body: { userId } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body.users;
    },
    onSuccess: () => invalidateReviewers(qc, session),
  });
}

export function useUnassignManualUser(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: number; userId: number }) => {
      const res = await manualApi.unassignManualUser({ params: { id }, body: { userId } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body.users;
    },
    onSuccess: () => invalidateReviewers(qc, session),
  });
}

/** The manual's audit timeline (ADR-015 Phase B, read-only) — lazily fetched when the details drawer opens (`id` set).
 *  Newest-first; the server strips the forensic actor fields. Keyed per (realm, user, manual) so it refetches per manual. */
export function useManualRatings(session: AuthSession | null, id: number | null) {
  return useQuery({
    queryKey: ["manual-ratings", session?.realm, session?.sub, id],
    enabled: session !== null && id !== null,
    queryFn: async () => {
      const res = await manualApi.getManualRatings({ params: { id: id! } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body; // { average, count, own, ratings }
    },
  });
}

/** The manual BODY (ADR-002 Phase 4) — sections → typed steps for the runtime viewer. Its own cache key so opening the
 *  viewer doesn't disturb the ratings/links reads. */
export function useManualBody(session: AuthSession | null, id: number | null, enabled = true) {
  return useQuery({
    queryKey: ["manual-body", session?.realm, session?.sub, id],
    // `enabled` lets a caller LAZILY pull the outline — the mark dialog only needs it if the reviewer actually wants
    // to name a section, and a whole-manual flag should not pay for a body fetch (ADR-041 D5).
    enabled: session !== null && id !== null && enabled,
    queryFn: () => getManualBody(id!),
  });
}

/** The fetch itself, shared by the hook and {@link fetchManualBody} so a declarative read and an imperative one cannot
 *  drift into fetching different things or healing errors differently. */
async function getManualBody(id: number): Promise<ManualBody> {
  const res = await manualApi.getManualBody({ params: { id } });
  if (res.status !== 200) throw fail(res.status, res.body);
  return res.body; // { manual, capabilities, sections }
}

/**
 * Fetch a body IMPERATIVELY, into the same cache the editor reads (ADR-018 addendum §9).
 *
 * For the manuals list's Check Manual gate: the list holds summaries, so the check's input has to be pulled at the
 * moment the author asks to publish. **`staleTime: 0` forces a real fetch** rather than accepting the 30 s default —
 * a pre-publish summary computed from a body that may be half a minute old can pass a manual whose last step was
 * just emptied from Unity or the mobile app, which is exactly the class of mistake the check exists to catch.
 */
export function fetchManualBody(
  qc: ReturnType<typeof useQueryClient>,
  session: AuthSession | null,
  id: number,
): Promise<ManualBody> {
  return qc.fetchQuery({
    queryKey: ["manual-body", session?.realm, session?.sub, id],
    queryFn: () => getManualBody(id),
    staleTime: 0,
  });
}

// ── Manual body AUTHORING (ADR-002 Phase 9/10, RED-ZONE API) ──────────────────────────────────────────────────────
/** Force-refetch the manual BODY (`["manual-body", …, id]`) — e.g. after a status transition off `draft`, so the editor
 *  re-derives read-only from the fresh `manual.state` (the shipped transition hooks invalidate the manuals LISTS but not
 *  the body key, by design). Kept a plain helper (takes the client) so a component can fire it from a transition's onSuccess. */
export function invalidateManualBody(qc: ReturnType<typeof useQueryClient>, session: AuthSession | null, id: number | null): void {
  void qc.invalidateQueries({ queryKey: ["manual-body", session?.realm, session?.sub, id] });
}

/** One authoring command. Every body endpoint RETURNS the whole authoritative new body (a fresh `manual.version`), so on
 *  success we SEED it straight into the body cache — the cached body IS the editor's store, no optimistic-rollback
 *  bookkeeping — and invalidate the manuals lists (a body edit bumps `version` + `updated_at`/`lastUserUpdate`, shown on the
 *  card). Errors carry the contract `code` (MANUAL_BODY_STALE / BODY_ORDER_STALE / BODY_NAME_CONFLICT / MANUAL_NOT_EDITABLE /
 *  BLUEPRINT_UNAVAILABLE / STEP_PAYLOAD_TYPE_MISMATCH …) so the editor can reconcile a stale version or show inline copy. */
function useBodyMutation<TVars>(
  session: AuthSession | null,
  manualId: number | null,
  mutationFn: (vars: TVars) => Promise<ManualBody>,
) {
  const qc = useQueryClient();
  return useMutation<ManualBody, ManualMutationError, TVars>({
    mutationFn,
    onSuccess: (body) => {
      qc.setQueryData(["manual-body", session?.realm, session?.sub, manualId], body);
      void qc.invalidateQueries({ queryKey: ["facility-manuals", session?.realm, session?.sub] });
      invalidateDashboard(qc, session);
    },
  });
}

/** Every command takes `expectedVersion` (the optimistic CAS token — the cached body's `manual.version`). */
interface WithVersion {
  expectedVersion: number;
}

/**
 * The 12 fine-grained body-authoring commands (ADR-002 Phase 9) bundled for the editor — sections {create, rename, reorder,
 * delete}, steps {create, renameName, editText, reorder, move, delete}, and typed content {changeType, editPayload}. Each is
 * a TanStack mutation over `/manuals/:id/…`; a successful call seeds the returned body into the cache (see {@link useBodyMutation}).
 * Reorder/move send the WHOLE ordered sibling id-list (permutation → 409 BODY_ORDER_STALE); the DELETEs carry `expectedVersion`
 * as a query param (a DELETE has no body). Component-friendly `mutate({…})` vars; `manualId` scopes the cache seed.
 */
export function useManualBodyMutations(session: AuthSession | null, manualId: number | null) {
  const id = manualId ?? 0;
  const run = (res: { status: number; body: unknown }): ManualBody => {
    if (res.status !== 200) throw fail(res.status, res.body);
    return res.body as ManualBody;
  };

  // ── Sections ──
  const createSection = useBodyMutation(session, manualId, (v: WithVersion & { name: string }) =>
    manualApi.createSection({ params: { id }, body: v }).then(run),
  );
  const renameSection = useBodyMutation(session, manualId, (v: WithVersion & { sectionId: number; name: string }) =>
    manualApi.renameSection({ params: { id, sectionId: v.sectionId }, body: { expectedVersion: v.expectedVersion, name: v.name } }).then(run),
  );
  const reorderSections = useBodyMutation(session, manualId, (v: WithVersion & { order: number[] }) =>
    manualApi.reorderSections({ params: { id }, body: v }).then(run),
  );
  const deleteSection = useBodyMutation(session, manualId, (v: WithVersion & { sectionId: number }) =>
    manualApi.deleteSection({ params: { id, sectionId: v.sectionId }, query: { expectedVersion: v.expectedVersion } }).then(run),
  );

  // ── Steps (structure) ──
  const createStep = useBodyMutation(session, manualId, (v: WithVersion & { sectionId: number; name: string }) =>
    manualApi.createStep({ params: { id, sectionId: v.sectionId }, body: { expectedVersion: v.expectedVersion, name: v.name } }).then(run),
  );
  const renameStep = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; name: string }) =>
    manualApi.renameStep({ params: { id, stepId: v.stepId }, body: { expectedVersion: v.expectedVersion, name: v.name } }).then(run),
  );
  const reorderSteps = useBodyMutation(session, manualId, (v: WithVersion & { sectionId: number; order: number[] }) =>
    manualApi.reorderSteps({ params: { id, sectionId: v.sectionId }, body: { expectedVersion: v.expectedVersion, order: v.order } }).then(run),
  );
  const moveStep = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; toSectionId: number; order: number[] }) =>
    manualApi.moveStep({ params: { id, stepId: v.stepId }, body: { expectedVersion: v.expectedVersion, toSectionId: v.toSectionId, order: v.order } }).then(run),
  );
  const deleteStep = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number }) =>
    manualApi.deleteStep({ params: { id, stepId: v.stepId }, query: { expectedVersion: v.expectedVersion } }).then(run),
  );

  // ── Steps (content) ──
  const editStepText = useBodyMutation(
    session,
    manualId,
    (v: WithVersion & { stepId: number; standard?: string | null; completed?: string | null; failed?: string | null }) =>
      manualApi
        .editStepText({
          params: { id, stepId: v.stepId },
          body: { expectedVersion: v.expectedVersion, standard: v.standard, completed: v.completed, failed: v.failed },
        })
        .then(run),
  );
  /** The legacy `Auswählen`: re-point the step's main element at a blueprint. The step TYPE is derived server-side
   *  from the chosen blueprint, so it is deliberately absent from the request. */
  const setStepMainElement = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; blueprintId: number }) =>
    manualApi.setStepMainElement({ params: { id, stepId: v.stepId }, body: { expectedVersion: v.expectedVersion, blueprintId: v.blueprintId } }).then(run),
  );

  /** The legacy `+`: clone a blueprint onto the step as a non-main element. */
  // `sensorId` names the PHYSICAL DEVICE for a sensor-category blueprint (ADR-038) — required there, refused
  // elsewhere, and validated server-side against the blueprint's own type.
  const addStepElement = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; blueprintId: number; sensorId?: number }) =>
    manualApi
      .addStepElement({ params: { id, stepId: v.stepId }, body: { expectedVersion: v.expectedVersion, blueprintId: v.blueprintId, sensorId: v.sensorId } })
      .then(run),
  );

  /** Remove a non-main element. The CAS token rides the QUERY — a DELETE body is stripped by fetch stacks + gravitee. */
  const removeStepElement = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; elementId: number }) =>
    manualApi.removeStepElement({ params: { id, stepId: v.stepId, elementId: v.elementId }, query: { expectedVersion: v.expectedVersion } }).then(run),
  );

  const changeStepType = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; type: ManualStepType }) =>
    manualApi.changeStepType({ params: { id, stepId: v.stepId }, body: { expectedVersion: v.expectedVersion, type: v.type } }).then(run),
  );
  const editStepPayload = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; payload: StepPayload }) =>
    manualApi.editStepPayload({ params: { id, stepId: v.stepId }, body: { expectedVersion: v.expectedVersion, payload: v.payload } }).then(run),
  );
  // Attach a Master-library file to a step's media (ADR-024 Phase 11a-4); the server derives the media kind from the file.
  /** Set ONE element setting (ADR-027 Phase B). RED ZONE server-side — the client only carries the intent. */
  const setElementSetting = useBodyMutation(
    session,
    manualId,
    (v: WithVersion & { stepId: number; elementId: number; name: string; value?: string | null; fileIds?: number[] }) =>
      manualApi
        .setElementSetting({
          params: { id, stepId: v.stepId, elementId: v.elementId, name: v.name },
          body: { expectedVersion: v.expectedVersion, ...(v.value !== undefined ? { value: v.value } : {}), ...(v.fileIds !== undefined ? { fileIds: v.fileIds } : {}) },
        })
        .then(run),
  );

  // ── ADR-028: the manual-level ("Alle Schritte") twins of the five element writes. ──
  const addManualElement = useBodyMutation(session, manualId, (v: WithVersion & { blueprintId: number }) =>
    manualApi.addManualElement({ params: { id }, body: { expectedVersion: v.expectedVersion, blueprintId: v.blueprintId } }).then(run),
  );
  const removeManualElement = useBodyMutation(session, manualId, (v: WithVersion & { elementId: number }) =>
    manualApi.removeManualElement({ params: { id, elementId: v.elementId }, query: { expectedVersion: v.expectedVersion } }).then(run),
  );
  const duplicateManualElement = useBodyMutation(session, manualId, (v: WithVersion & { elementId: number }) =>
    manualApi.duplicateManualElement({ params: { id, elementId: v.elementId }, body: { expectedVersion: v.expectedVersion } }).then(run),
  );
  const setManualElementSetting = useBodyMutation(session, manualId, (v: WithVersion & { elementId: number; name: string; value?: string | null; fileIds?: number[] }) =>
    manualApi
      .setManualElementSetting({ params: { id, elementId: v.elementId, name: v.name }, body: { expectedVersion: v.expectedVersion, ...(v.value !== undefined ? { value: v.value } : {}), ...(v.fileIds !== undefined ? { fileIds: v.fileIds } : {}) } })
      .then(run),
  );
  const copyManualElementSettings = useBodyMutation(session, manualId, (v: WithVersion & { elementId: number; sourceElementId: number }) =>
    manualApi.copyManualElementSettings({ params: { id, elementId: v.elementId, sourceElementId: v.sourceElementId }, body: { expectedVersion: v.expectedVersion } }).then(run),
  );

  /** The legacy `Element duplizieren` — a deep copy onto the same step (settings, files, geometry). */
  const duplicateStepElement = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; elementId: number }) =>
    manualApi.duplicateStepElement({ params: { id, stepId: v.stepId, elementId: v.elementId }, body: { expectedVersion: v.expectedVersion } }).then(run),
  );

  /** The legacy `Einstellungen einfügen` — one transaction, one version bump, not N single writes. */
  const copyElementSettings = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; elementId: number; sourceElementId: number }) =>
    manualApi
      .copyElementSettings({ params: { id, stepId: v.stepId, elementId: v.elementId, sourceElementId: v.sourceElementId }, body: { expectedVersion: v.expectedVersion } })
      .then(run),
  );

  const attachStepMedia = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; fileId: number }) =>
    manualApi.attachStepMedia({ params: { id, stepId: v.stepId }, body: { expectedVersion: v.expectedVersion, fileId: v.fileId } }).then(run),
  );
  /** Remove a step's media (ADR-027 §9 — the legacy tile's ×). The CAS token rides the QUERY like every DELETE here. */
  const detachStepMedia = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number }) =>
    manualApi.detachStepMedia({ params: { id, stepId: v.stepId }, query: { expectedVersion: v.expectedVersion } }).then(run),
  );
  // Save an edited step image straight onto the step (ADR-025 edit-attach) — the server validates + lands it (copy-on-write)
  // and returns the updated body, which the gate advances the CAS version from + seeds into cache (like every body write).
  const saveEditedStepImage = useBodyMutation(session, manualId, (v: WithVersion & { stepId: number; file: File }) =>
    saveEditedStepImageApi(id, v.stepId, v.file, v.expectedVersion),
  );

  // Video CUT (ADR-025 A4) — an ASYNC command, NOT a version-CAS body write: it 202s (no `expectedVersion`, no returned body),
  // the worker cuts + the API lands it later. So it's a plain mutation (not `useBodyMutation`); on success we best-effort
  // invalidate the body so a fast land shows. The trim UI supplies the window + optional label; the server derives the rest.
  const qc = useQueryClient();
  const cutStepVideo = useMutation<CutStepVideoAccepted, ManualMutationError, { stepId: number; sourceFileId: number; startSeconds: number; durationSeconds: number; name?: string }>({
    mutationFn: async (v) => {
      const res = await manualApi.cutStepVideo({
        params: { id, stepId: v.stepId },
        body: { sourceFileId: v.sourceFileId, startSeconds: v.startSeconds, durationSeconds: v.durationSeconds, ...(v.name ? { name: v.name } : {}) },
      });
      if (res.status !== 202) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["manual-body", session?.realm, session?.sub, manualId] });
      // ALSO refresh the background-job list (diff-panel R9-F4). The socket only pushes `media:job` on a TRANSITION, and
      // the row is created by this very request — so nothing pushes for the enqueue itself. Without this the new job is
      // invisible (no header entry, no per-step "queued" badge) until the 60s safety-net poll, which reads as "my cut did
      // nothing" for up to a minute — precisely the gap ADR-026 Phase 6 exists to close.
      void qc.invalidateQueries({ queryKey: ["media-jobs", session?.realm, session?.sub] });
    },
  });

  return {
    createSection,
    renameSection,
    reorderSections,
    deleteSection,
    createStep,
    renameStep,
    reorderSteps,
    moveStep,
    deleteStep,
    editStepText,
    setStepMainElement,
    addStepElement,
    removeStepElement,
    setElementSetting,
    copyElementSettings,
    duplicateStepElement,
    addManualElement,
    removeManualElement,
    duplicateManualElement,
    setManualElementSetting,
    copyManualElementSettings,
    changeStepType,
    editStepPayload,
    attachStepMedia,
    detachStepMedia,
    saveEditedStepImage,
    cutStepVideo,
  };
}

/** Upsert MY rating for a manual (ADR-002 Phase 7 — the rate-on-finish panel). Over the shipped `PUT /manuals/:id/rating`;
 *  the 200 body is the fresh ratings summary, which we seed into the `manual-ratings` cache so the start-page avg/comments
 *  reflect it immediately. Errors carry the contract `code` (RATING_FORBIDDEN / MANUAL_NOT_RATEABLE) for inline copy. */
export function useUpsertManualRating(session: AuthSession | null, id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { rating: number; comment?: string | null }) => {
      const res = await manualApi.upsertManualRating({ params: { id: id! }, body: input });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body; // ManualRatings
    },
    onSuccess: (data) => {
      qc.setQueryData(["manual-ratings", session?.realm, session?.sub, id], data);
      invalidateDashboard(qc, session); // the Ratings page's stars + the Übersicht Ø (ADR-034)
    },
  });
}

export function useManualLinks(session: AuthSession | null, id: number | null) {
  return useQuery({
    queryKey: ["manual-links", session?.realm, session?.sub, id],
    enabled: session !== null && id !== null,
    queryFn: async () => {
      const res = await manualApi.getManualLinks({ params: { id: id! } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body; // { parent, children }
    },
  });
}

/** A create/unlink changes both the manuals lists/counts AND any open drawer's `manual-links` — invalidate both. */
function invalidateManualsAndLinks(qc: ReturnType<typeof useQueryClient>, session: AuthSession | null): void {
  invalidateManuals(qc, session);
  void qc.invalidateQueries({ queryKey: ["manual-links", session?.realm, session?.sub] });
}

export interface CreateManualLinkVars {
  /** The SOURCE manual being linked (the `:id`). */
  sourceId: number;
  /** The TARGET facility + category the linked copy is created into (create-and-place atomic — ADR-020). */
  facilityId: number;
  categoryId: number;
}

/** Create a linked copy of a (published) source manual directly in a target facility/category (ADR-020, create-and-place
 *  atomic). Invalidates the manuals lists/counts + any open `manual-links`. */
export function useCreateManualLink(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sourceId, facilityId, categoryId }: CreateManualLinkVars) => {
      const res = await manualApi.createManualLink({ params: { id: sourceId }, body: { facilityId, categoryId } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body; // the new child Manual
    },
    onSuccess: () => invalidateManualsAndLinks(qc, session),
  });
}

/** Remove a linked copy (ADR-020) — hard-deletes the linked child. Invalidates the manuals lists/counts + `manual-links`. */
export function useUnlinkManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await manualApi.unlinkManual({ params: { id }, body: {} });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body; // { deleted }
    },
    onSuccess: () => invalidateManualsAndLinks(qc, session),
  });
}

export interface DeleteManualVars {
  id: number;
  /** The opaque snapshot from `useManualStats` — the server re-checks it under the lock and 409s if the manual changed
   *  (rename / move / archive / category-rename) since the confirmation. */
  expected: string;
}

export function useDeleteManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expected }: DeleteManualVars) => {
      const res = await manualApi.deleteManual({ params: { id }, query: { expected } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body; // { deleted: number }
    },
    onSuccess: () => invalidateManuals(qc, session),
  });
}

// ── Review state machine (ADR-018) ────────────────────────────────────────────────────────────────────────────
/** A review transition — carries the state the client BELIEVES the manual is in (`expectedFrom`), the server's atomic
 *  compare-and-set guard: a 409 `MANUAL_STATE_STALE` means it moved under us (refetch shows the truth). */
export interface TransitionManualVars {
  id: number;
  expectedFrom: ManualState;
}
export interface MarkManualVars extends TransitionManualVars {
  /** The free-text reason the confirmed manual is being flagged as faulty. */
  comment: string;
  /** WHERE the fault is (ADR-041). Ids, never names — the server resolves them against the manual's content owner
   *  and stores the resolved names, so a report cannot carry a location that does not exist. Both omitted means
   *  "the whole manual". */
  sectionId?: number;
  stepId?: number;
}

/** draft → ready_for_review. */
export function useSubmitReviewManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom }: TransitionManualVars) => {
      const res = await manualApi.submitReviewManual({ params: { id }, body: { expectedFrom } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => invalidateManuals(qc, session),
  });
}

/** ready_for_review → confirmed (publish). */
export function useConfirmManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom }: TransitionManualVars) => {
      const res = await manualApi.confirmManual({ params: { id }, body: { expectedFrom } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => invalidateManuals(qc, session),
  });
}

/** confirmed → marked (flag a fault, with a comment). */
export function useMarkManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom, comment, sectionId, stepId }: MarkManualVars) => {
      const res = await manualApi.markManual({ params: { id }, body: { expectedFrom, comment, sectionId, stepId } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: (_data, vars) => {
      invalidateManuals(qc, session);
      // …and the BODY, which carries the manual's own state. The mark dialog's location picker now populates that
      // cache key (ADR-041 D5's lazy outline fetch) and the viewer reads the same one, so without this a manual
      // marked from the list still reads `confirmed` in the viewer for the whole 30s staleTime (diff panel).
      void qc.invalidateQueries({ queryKey: ["manual-body", session?.realm, session?.sub, vars.id] });
    },
  });
}

/** marked → confirmed (clear the flag). */
export function useUnmarkManual(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom }: TransitionManualVars) => {
      const res = await manualApi.unmarkManual({ params: { id }, body: { expectedFrom } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => invalidateManuals(qc, session),
  });
}

/** → draft (withdraw from review / reopen a confirmed manual). */
export function useReturnManualToDraft(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expectedFrom }: TransitionManualVars) => {
      const res = await manualApi.returnManualToDraft({ params: { id }, body: { expectedFrom } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => invalidateManuals(qc, session),
  });
}
